import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Event } from '../types';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Plus, Trash2, Pencil, MapPin, LogOut, Briefcase, Sparkles, Database, Users, Menu, X, Settings, BookOpen, AlertTriangle, ExternalLink } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export default function EventsList() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');

  // Edit Event State
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editName, setEditName] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const isSuperAdmin = Boolean(user?.isSuperAdmin || (user as any)?.role === 'superadmin');

  useEffect(() => {
    const q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];
      
      if (user?.role === 'hostess') {
        const allowed = user.hostessEvents || [];
        data = data.filter(e => e.id && allowed.includes(e.id));
      }
      
      setEvents(data);
      setLoading(false);
      setQuotaError(null);
    }, (error: any) => {
      setLoading(false);
      if (error?.message?.includes('Quota') || error?.code === 'resource-exhausted') {
        setQuotaError("Limita gratuită zilnică de citiri Firestore (Free daily read units) a fost depășită pentru acest proiect.");
      } else {
        console.warn("EventsList onSnapshot notice:", error?.message || error);
      }
    });
    return unsubscribe;
  }, []);

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !date) return;
    
    try {
      await addDoc(collection(db, 'events'), {
        name: name.trim(),
        date,
        time: time.trim(),
        location: location.trim(),
        description: description.trim(),
        createdAt: Date.now()
      });
      setIsAdding(false);
      setName('');
      setDate('');
      setTime('');
      setLocation('');
      setDescription('');
    } catch (err) {
      console.error(err);
      alert('Eroare la crearea evenimentului');
    }
  };

  const handleStartEdit = (eventToEdit: Event, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSuperAdmin) {
      alert('Doar utilizatorii cu rol de SuperAdmin au permisiunea de a edita un eveniment.');
      return;
    }
    setEditingEvent(eventToEdit);
    setEditName(eventToEdit.name || eventToEdit.title || '');
    setEditDate(eventToEdit.date || '');
    setEditTime(eventToEdit.time || '');
    setEditLocation(eventToEdit.location || '');
    setEditDescription(eventToEdit.description || '');
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent?.id || !editName.trim() || !editDate) return;
    if (!isSuperAdmin) {
      alert('Doar utilizatorii cu rol de SuperAdmin au permisiunea de a edita un eveniment.');
      return;
    }

    setSavingEdit(true);
    try {
      await updateDoc(doc(db, 'events', editingEvent.id), {
        name: editName.trim(),
        date: editDate,
        time: editTime.trim(),
        location: editLocation.trim(),
        description: editDescription.trim(),
        updatedAt: Date.now()
      });
      setEditingEvent(null);
    } catch (err) {
      console.error('Eroare la actualizarea evenimentului:', err);
      alert('A apărut o eroare la salvarea modificărilor evenimentului.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSuperAdmin) {
      alert('Doar utilizatorii cu rol de SuperAdmin au permisiunea de a șterge un eveniment.');
      return;
    }
    if (confirm('Sigur doriți să ștergeți acest eveniment? Această acțiune nu șterge automat invitații asociați.')) {
      try {
        await deleteDoc(doc(db, 'events', id));
      } catch (err) {
        console.error('Eroare la ștergerea evenimentului:', err);
        alert('A apărut o eroare la ștergerea evenimentului.');
      }
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600 font-medium">Se încarcă evenimentele...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navigation Bar */}
      <header className="h-16 border-b border-slate-200 bg-white px-6 md:px-12 flex justify-between items-center sticky top-0 z-10 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-black rounded-full-full flex items-center justify-center shadow-xs">
            <span className="text-white font-bold text-sm tracking-tight">EM</span>
          </div>
          <div>
            <span className="font-semibold text-slate-800 tracking-tight text-base block leading-tight">EventMaster</span>
            <span className="text-[11px] font-medium text-[#E2231A] tracking-wide uppercase">by Marketing Matters</span>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {user?.role === 'admin' && (
            <>
              {user?.isSuperAdmin && (
                <>
                  <Link
                    to="/mktm-team"
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    <Users className="w-3.5 h-3.5 text-slate-500" />
                    Echipa MKTM
                  </Link>
                  <Link
                    to="/team"
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    <Briefcase className="w-3.5 h-3.5 text-slate-500" />
                    Echipa Lenovo
                  </Link>
                </>
              )}
              <Link
                to="/clients"
                className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <Database className="w-3.5 h-3.5 text-slate-500" />
                Baza Date Clienți
              </Link>
              {user?.isSuperAdmin && (
                <Link
                  to="/settings"
                  className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <Settings className="w-3.5 h-3.5 text-[#E2231A]" />
                  Integrări
                </Link>
              )}
              <Link
                to="/help"
                className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <BookOpen className="w-3.5 h-3.5 text-[#E2231A]" />
                Manual
              </Link>
            </>
          )}
          <div className="text-right hidden md:block">
            <div className="flex items-center gap-1.5 justify-end">
              <span className="text-xs font-semibold text-slate-800 block leading-tight">{user?.name}</span>
              {user?.isSuperAdmin && (
                <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-800 uppercase">SuperAdmin</span>
              )}
            </div>
            <span className="text-[11px] text-slate-400 capitalize">{user?.role}</span>
          </div>
          <button
            onClick={signOut}
            title="Deconectare"
            className="hidden sm:flex p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
          </button>

          {/* Mobile hamburger button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Meniu Mobil"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6 text-[#E2231A]" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-xs" 
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="relative ml-auto w-full max-w-xs bg-white h-full shadow-2xl flex flex-col z-10 overflow-y-auto p-5">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h2 className="font-bold text-slate-900 text-base">Meniu Admin</h2>
                <span className="text-xs text-slate-500">{user?.name} ({user?.role})</span>
              </div>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="py-4 space-y-2 flex-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block px-2">
                Secțiuni Globale
              </span>
              {user?.role === 'admin' && (
                <>
                  {user?.isSuperAdmin && (
                    <>
                      <Link
                        to="/mktm-team"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                      >
                        <Users className="w-4 h-4 text-[#E2231A]" /> Echipa MKTM
                      </Link>
                      <Link
                        to="/team"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                      >
                        <Briefcase className="w-4 h-4 text-[#E2231A]" /> Echipa Lenovo
                      </Link>
                    </>
                  )}
                  <Link
                    to="/clients"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <Database className="w-4 h-4 text-[#E2231A]" /> Baza Date Clienți
                  </Link>
                  {user?.isSuperAdmin && (
                    <Link
                      to="/settings"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      <Settings className="w-4 h-4 text-[#E2231A]" /> Setări Integrări
                    </Link>
                  )}
                  <Link
                    to="/help"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <BookOpen className="w-4 h-4 text-[#E2231A]" /> Manual de Utilizare
                  </Link>
                </>
              )}
            </div>

            <div className="pt-4 border-t border-slate-100">
              <button
                onClick={signOut}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 text-[#C41A12] hover:bg-red-100 font-bold text-sm transition-colors"
              >
                <LogOut className="w-4 h-4" /> Deconectare
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="p-6 md:p-12 flex-1">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-[#C41A12] mb-2">
                <Sparkles className="w-3 h-3" /> Management Evenimente
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">EventMaster by Marketing Matters</h1>
              <p className="text-slate-500 mt-2 text-sm md:text-base">Selectați un eveniment pentru a-l gestiona sau creați unul nou.</p>
            </div>
            {user?.role === 'admin' && (
              <button 
                onClick={() => setIsAdding(!isAdding)}
                className="bg-black text-white px-4 py-2.5 rounded-full-full font-medium hover:bg-[#1F1F1F] transition-all flex items-center gap-2 shadow-xs cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                Eveniment Nou
              </button>
            )}
          </div>

          {quotaError && (
            <div className="mb-8 p-5 bg-amber-50 border border-amber-200 rounded-2xl text-amber-900 shadow-xs">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm leading-relaxed">
                  <div className="font-bold text-base text-amber-950">
                    Limita de citiri zilnice Firestore a fost atinsă (Quota Limit Exceeded)
                  </div>
                  <p className="text-amber-800">
                    Baza de date Firestore a atins plafonul gratuit zilnic de citiri (Free daily read units per project). Operațiunile de citire sunt restricționate temporar până la resetarea zilnică a cotei (la miezul nopții UTC) sau până la activarea planului de plată (Blaze/Enterprise).
                  </p>
                  <div className="pt-2">
                    <a
                      href="https://console.firebase.google.com/project/gen-lang-client-0127508671/firestore/databases/ai-studio-8a440130-5a91-406e-b572-704cc398089e/data?openUpgradeDialog=true"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs rounded-xl shadow-xs transition-colors"
                    >
                      <span>Deschide Consola Firebase pentru Upgrade Plan</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

        {isAdding && user?.role === 'admin' && (
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-8">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Detalii Eveniment Nou</h2>
            <form onSubmit={handleAddEvent} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nume Eveniment *</label>
                <input required type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data *</label>
                <input required type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-4 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ora Evenimentului</label>
                <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full px-4 py-2 border rounded-md" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Locație</label>
                <input type="text" value={location} onChange={e => setLocation(e.target.value)} className="w-full px-4 py-2 border rounded-md" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Descriere scurtă</label>
                <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="w-full px-4 py-2 border rounded-md" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 mt-2">
                <button type="button" onClick={() => setIsAdding(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-md font-medium">Anulează</button>
                <button type="submit" className="px-4 py-2 bg-black text-white rounded-full-full font-medium hover:bg-[#1F1F1F]">Salvează Evenimentul</button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.length === 0 ? (
            <div className="col-span-full bg-white p-12 text-center rounded-xl border border-slate-200 text-slate-500">
              Nu există evenimente create. Creați primul eveniment pentru a începe.
            </div>
          ) : (
            events.map(event => (
              <div 
                key={event.id}
                onClick={() => {
                  if (user?.role === 'admin') {
                    navigate(`/events/${event.id}/dashboard`);
                  } else if (user?.role === 'hostess') {
                    navigate(`/events/${event.id}/scanner`);
                  } else {
                    navigate(`/events/${event.id}/partner`);
                  }
                }}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-red-100 text-[#E2231A] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CalendarIcon className="w-6 h-6" />
                  </div>
                  {isSuperAdmin && (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button 
                        type="button"
                        onClick={(e) => handleStartEdit(event, e)} 
                        className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                        title="Editează evenimentul (Doar SuperAdmin)"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button 
                        type="button"
                        onClick={(e) => handleDelete(event.id!, e)} 
                        className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 p-2 rounded-lg transition-colors"
                        title="Șterge evenimentul (Doar SuperAdmin)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{event.name}</h3>
                <div className="text-sm text-slate-500 flex flex-col gap-2 mt-auto">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 shrink-0" />
                    <span>
                      {new Date(event.date).toLocaleDateString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      {event.time ? ` • Ora ${event.time}` : ''}
                    </span>
                  </div>
                  {event.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {event.location}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      </main>

      {/* Modal Editare Eveniment - SuperAdmin Only */}
      {editingEvent && isSuperAdmin && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => !savingEdit && setEditingEvent(null)}
        >
          <div 
            className="bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Pencil className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-base">Editează Evenimentul</h3>
                  <span className="text-xs text-slate-400">Doar SuperAdmin</span>
                </div>
              </div>
              <button 
                type="button"
                disabled={savingEdit}
                onClick={() => setEditingEvent(null)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                title="Închide"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateEvent} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nume Eveniment *</label>
                  <input 
                    required 
                    type="text" 
                    value={editName} 
                    onChange={e => setEditName(e.target.value)} 
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" 
                    placeholder="Ex: Lenovo Tech Day 2026"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data *</label>
                  <input 
                    required 
                    type="date" 
                    value={editDate} 
                    onChange={e => setEditDate(e.target.value)} 
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ora Evenimentului</label>
                  <input 
                    type="time" 
                    value={editTime} 
                    onChange={e => setEditTime(e.target.value)} 
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" 
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Locație</label>
                  <input 
                    type="text" 
                    value={editLocation} 
                    onChange={e => setEditLocation(e.target.value)} 
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" 
                    placeholder="Ex: Hotel Marriott, Sala Grand Ballroom"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Descriere scurtă</label>
                  <textarea 
                    rows={3}
                    value={editDescription} 
                    onChange={e => setEditDescription(e.target.value)} 
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E2231A] focus:border-transparent resize-none" 
                    placeholder="Detalii suplimentare despre eveniment..."
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button 
                  type="button" 
                  disabled={savingEdit}
                  onClick={() => setEditingEvent(null)} 
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg font-semibold transition-colors cursor-pointer"
                >
                  Anulează
                </button>
                <button 
                  type="submit" 
                  disabled={savingEdit || !editName.trim() || !editDate}
                  className="px-5 py-2 text-sm bg-black text-white rounded-full-full font-semibold hover:bg-[#1F1F1F] transition-colors disabled:opacity-50 flex items-center gap-2 cursor-pointer shadow-xs"
                >
                  {savingEdit ? 'Se salvează...' : 'Salvează Modificările'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
